import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Select,
  MenuItem,
  ListItemIcon,
  ListItemText,
  FormControl,
  InputLabel,
  Avatar,
} from '@mui/material';
import {
  Settings,
  Add,
  Delete,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useSnackbar } from 'notistack';
import { 
  getStoreConfigAsync, 
  saveStoreConfigAsync, 
  resetStoreConfigAsync 
} from '../../../../utils/storeConfig';

// Category colors mapping for distinct pill colors
const CATEGORY_COLORS = {
  'food': { bg: '#ffebee', color: '#c62828' },
  'entertainment': { bg: '#f3e5f5', color: '#7b1fa2' },
  'electronics': { bg: '#e8f5e8', color: '#2e7d32' },
  'clothing': { bg: '#fff3e0', color: '#ef6c00' },
  'books': { bg: '#e3f2fd', color: '#1565c0' },
  'games': { bg: '#fce4ec', color: '#ad1457' },
  'music': { bg: '#f1f8e9', color: '#558b2f' },
  'sports': { bg: '#fff8e1', color: '#f57f17' },
  'default': { bg: '#f5f5f5', color: '#616161' }
};

// Get category color based on item name or type
const getCategoryColor = (itemName, points) => {
  const name = itemName.toLowerCase();
  
  if (name.includes('food') || name.includes('snack') || name.includes('racetrac')) {
    return CATEGORY_COLORS.food;
  } else if (name.includes('game') || name.includes('switch') || name.includes('stardew') || name.includes('smash') || name.includes('trombone')) {
    return CATEGORY_COLORS.games;
  } else if (name.includes('book') || name.includes('harry potter')) {
    return CATEGORY_COLORS.books;
  } else if (name.includes('electronic') || name.includes('device')) {
    return CATEGORY_COLORS.electronics;
  } else if (name.includes('music') || name.includes('song')) {
    return CATEGORY_COLORS.music;
  } else if (name.includes('sport') || name.includes('activity')) {
    return CATEGORY_COLORS.sports;
  } else if (points >= 100) {
    return CATEGORY_COLORS.entertainment;
  } else {
    return CATEGORY_COLORS.default;
  }
};

// Available images for store items
const AVAILABLE_IMAGES = [
  { path: '/images/racetrac.png', name: 'RaceTrac Store' },
  { path: '/images/switch.png', name: 'Nintendo Switch' },
  { path: '/images/phone.jpg', name: 'Phone' },
  { path: '/images/prius.png', name: 'Prius Car' },
  { path: '/images/truck.png', name: 'Truck' },
  { path: '/images/lunch.png', name: 'School Lunch' },

];

const StoreConfiguration = ({ storeConfig, onStoreConfigUpdated }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [storeConfigDialog, setStoreConfigDialog] = useState(false);
  const [editingStoreConfig, setEditingStoreConfig] = useState(null);
  const [editingStoreItem, setEditingStoreItem] = useState(null);
  const [addingStoreItem, setAddingStoreItem] = useState(false);
  const [customImagePath, setCustomImagePath] = useState('');

  const openStoreConfigDialog = () => {
    setEditingStoreConfig(JSON.parse(JSON.stringify(storeConfig))); // Deep copy
    setStoreConfigDialog(true);
  };

  const handleSaveStoreConfig = async () => {
    try {
      const saved = await saveStoreConfigAsync(editingStoreConfig);
      if (saved) {
        setStoreConfigDialog(false);
        setEditingStoreConfig(null);
        enqueueSnackbar('Store configuration saved to Firestore!', { variant: 'success' });
        if (onStoreConfigUpdated) {
          onStoreConfigUpdated();
        }
      }
    } catch (error) {
      enqueueSnackbar('Failed to save store configuration', { variant: 'error' });
    }
  };

  const handleResetStoreConfig = async () => {
    if (!window.confirm('Reset store configuration to defaults? This cannot be undone.')) {
      return;
    }
    
    try {
      const reset = await resetStoreConfigAsync();
      if (reset) {
        const defaultConfig = await getStoreConfigAsync(); // Get defaults from Firestore
        setEditingStoreConfig(defaultConfig);
        enqueueSnackbar('Store configuration reset to defaults', { variant: 'success' });
        if (onStoreConfigUpdated) {
          onStoreConfigUpdated();
        }
      }
    } catch (error) {
      enqueueSnackbar('Failed to reset store configuration', { variant: 'error' });
    }
  };

  const handleEditStoreItem = (item) => {
    setEditingStoreItem({ ...item });
    // Initialize custom path if the current image isn't in available images
    if (item.image && !AVAILABLE_IMAGES.find(img => img.path === item.image)) {
      setCustomImagePath(item.image);
    } else {
      setCustomImagePath('');
    }
  };

  const handleSaveStoreItem = () => {
    if (!editingStoreItem.name.trim() || editingStoreItem.price < 0) {
      enqueueSnackbar('Please provide valid name and price', { variant: 'error' });
      return;
    }

    const updated = [...editingStoreConfig];
    const itemIndex = updated.findIndex(item => item.id === editingStoreItem.id);
    if (itemIndex >= 0) {
      updated[itemIndex] = { ...editingStoreItem };
      setEditingStoreConfig(updated);
      setEditingStoreItem(null);
      enqueueSnackbar('Store item updated!', { variant: 'success' });
    }
  };

  const handleAddStoreItem = () => {
    setEditingStoreItem({
      id: Math.max(...editingStoreConfig.map(item => item.id), 0) + 1,
      name: '',
      description: '',
      price: 50,
      icon: 'Star',
      image: '',
      rarity: 'common',
      category: 'rewards',
      inStock: true,
      featured: false,
    });
    setAddingStoreItem(true);
    setCustomImagePath('');
  };

  const handleSaveNewStoreItem = () => {
    if (!editingStoreItem.name.trim() || editingStoreItem.price < 0) {
      enqueueSnackbar('Please provide valid name and price', { variant: 'error' });
      return;
    }

    const updated = [...editingStoreConfig, { ...editingStoreItem }];
    setEditingStoreConfig(updated);
    setEditingStoreItem(null);
    setAddingStoreItem(false);
    enqueueSnackbar('Store item added!', { variant: 'success' });
  };

  const handleDeleteStoreItem = (itemId) => {
    if (!window.confirm('Delete this store item? This cannot be undone.')) {
      return;
    }

    const updated = editingStoreConfig.filter(item => item.id !== itemId);
    setEditingStoreConfig(updated);
    enqueueSnackbar('Store item deleted!', { variant: 'success' });
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
      >
        <Box
          sx={{
            bgcolor: '#f8f9fa',
            borderRadius: 4,
            p: 4,
            minHeight: '60vh'
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 4,
              pb: 3,
              borderBottom: '1px solid #e0e0e0'
            }}
          >
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Settings sx={{ color: '#666', fontSize: 28 }} />
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 'bold', 
                    color: '#333',
                    fontFamily: '"Roboto", sans-serif'
                  }}
                >
                  Store Configuration
                </Typography>
              </Box>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: '#666',
                  fontSize: '1rem',
                  opacity: 0.8
                }}
              >
                Manage store items, prices, and availability
              </Typography>
            </Box>
            
            <Button
              variant="contained"
              onClick={openStoreConfigDialog}
              startIcon={<Settings />}
              sx={{
                bgcolor: '#1976d2',
                color: 'white',
                px: 3,
                py: 1.5,
                borderRadius: 2,
                fontWeight: 'bold',
                textTransform: 'none',
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                '&:hover': { 
                  bgcolor: '#1565c0',
                  boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              Configure Store
            </Button>
          </Box>

          {/* Items Grid */}
          <Grid container spacing={2}>
            {storeConfig.map((item) => {
              const categoryStyle = getCategoryColor(item.name, item.price);
              return (
                <Grid item xs={12} sm={6} md={4} lg={2.4} key={item.id}>
                  <Card
                    sx={{
                      background: 'white',
                      borderRadius: 2,
                      p: 2,
                      height: '120px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      position: 'relative',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                      border: '1px solid rgba(0, 0, 0, 0.05)',
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                      }
                    }}
                  >
                    <Box>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: 'bold',
                          color: '#333',
                          fontSize: '1rem',
                          lineHeight: 1.2,
                          mb: 0.5
                        }}
                      >
                        {item.name}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#666',
                          fontSize: '0.875rem',
                          fontWeight: 500
                        }}
                      >
                        {item.price} points
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Chip
                        label={item.category || 'Item'}
                        sx={{
                          bgcolor: categoryStyle.bg,
                          color: categoryStyle.color,
                          fontWeight: 'bold',
                          fontSize: '0.65rem',
                          height: '24px',
                          borderRadius: '12px',
                          border: `1px solid ${categoryStyle.color}20`
                        }}
                      />
                    </Box>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </motion.div>

      {/* Store Configuration Dialog */}
      <Dialog
        open={storeConfigDialog}
        onClose={() => {
          setStoreConfigDialog(false);
          setEditingStoreConfig(null);
          setEditingStoreItem(null);
          setAddingStoreItem(false);
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Configure Store Items
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Manage store items, their prices, and availability.
          </Typography>
          
          {editingStoreConfig && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Store Items</Typography>
                <Button
                  variant="contained"
                  onClick={handleAddStoreItem}
                  startIcon={<Add />}
                  size="small"
                >
                  Add Item
                </Button>
              </Box>

              <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Price (Points)</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>In Stock</TableCell>
                      <TableCell>Featured</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {editingStoreConfig.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{item.name}</TableCell>
                        <TableCell>{item.description}</TableCell>
                        <TableCell>{item.price}</TableCell>
                        <TableCell>{item.category}</TableCell>
                        <TableCell>{item.inStock ? 'Yes' : 'No'}</TableCell>
                        <TableCell>{item.featured ? 'Yes' : 'No'}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              size="small"
                              onClick={() => handleEditStoreItem(item)}
                              variant="outlined"
                            >
                              Edit
                            </Button>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteStoreItem(item.id)}
                              color="error"
                            >
                              <Delete />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setStoreConfigDialog(false);
              setEditingStoreConfig(null);
              setEditingStoreItem(null);
              setAddingStoreItem(false);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleResetStoreConfig}
            color="warning"
          >
            Reset to Defaults
          </Button>
          <Button
            onClick={handleSaveStoreConfig}
            variant="contained"
            color="primary"
          >
            Save Configuration
          </Button>
        </DialogActions>
      </Dialog>

      {/* Store Item Edit Dialog */}
      <Dialog
        open={!!editingStoreItem}
        onClose={() => {
          setEditingStoreItem(null);
          setAddingStoreItem(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {addingStoreItem ? 'Add Store Item' : 'Edit Store Item'}
        </DialogTitle>
        <DialogContent>
          {editingStoreItem && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                label="Item Name"
                value={editingStoreItem.name}
                onChange={(e) => setEditingStoreItem({
                  ...editingStoreItem,
                  name: e.target.value
                })}
                fullWidth
                required
              />
              
              <TextField
                label="Description"
                value={editingStoreItem.description}
                onChange={(e) => setEditingStoreItem({
                  ...editingStoreItem,
                  description: e.target.value
                })}
                fullWidth
                multiline
                rows={2}
              />
              
              <TextField
                label="Price (Points)"
                type="number"
                value={editingStoreItem.price === 0 ? '' : editingStoreItem.price}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '') {
                    setEditingStoreItem({
                      ...editingStoreItem,
                      price: 0
                    });
                  } else {
                    setEditingStoreItem({
                      ...editingStoreItem,
                      price: parseInt(value) || 0
                    });
                  }
                }}
                inputProps={{ min: 0 }}
                fullWidth
                required
              />
              
              <TextField
                label="Category"
                value={editingStoreItem.category}
                onChange={(e) => setEditingStoreItem({
                  ...editingStoreItem,
                  category: e.target.value
                })}
                fullWidth
              />
              
              <FormControl fullWidth>
                <InputLabel>Image</InputLabel>
                <Select
                  value={
                    AVAILABLE_IMAGES.find(img => img.path === editingStoreItem.image) 
                      ? editingStoreItem.image 
                      : editingStoreItem.image ? 'custom' : ''
                  }
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === 'custom') {
                      setCustomImagePath(editingStoreItem.image || '');
                    } else {
                      setEditingStoreItem({
                        ...editingStoreItem,
                        image: value
                      });
                    }
                  }}
                  label="Image"
                  renderValue={(selected) => {
                    if (selected === 'custom') return 'Custom Path';
                    const selectedImage = AVAILABLE_IMAGES.find(img => img.path === selected);
                    return selectedImage ? selectedImage.name : selected || 'Select Image';
                  }}
                >
                  {AVAILABLE_IMAGES.map((image) => (
                    <MenuItem key={image.path} value={image.path}>
                      <ListItemIcon>
                        <Avatar
                          src={image.path}
                          sx={{ width: 32, height: 32, mr: 1 }}
                          variant="rounded"
                        />
                      </ListItemIcon>
                      <ListItemText 
                        primary={image.name}
                        secondary={image.path}
                      />
                    </MenuItem>
                  ))}
                  <MenuItem value="custom">
                    <ListItemIcon>
                      <Avatar
                        sx={{ width: 32, height: 32, mr: 1, bgcolor: 'grey.300' }}
                        variant="rounded"
                      >
                        📷
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText 
                      primary="Custom Path"
                      secondary="Enter your own image path"
                    />
                  </MenuItem>
                </Select>
              </FormControl>
              
              {editingStoreItem.image === 'custom' && (
                <TextField
                  label="Custom Image Path"
                  value={customImagePath}
                  onChange={(e) => {
                    setCustomImagePath(e.target.value);
                    setEditingStoreItem({
                      ...editingStoreItem,
                      image: e.target.value
                    });
                  }}
                  fullWidth
                  helperText="e.g., /images/custom-item.png"
                  placeholder="/images/"
                />
              )}
              
              <TextField
                select
                label="Rarity"
                value={editingStoreItem.rarity}
                onChange={(e) => setEditingStoreItem({
                  ...editingStoreItem,
                  rarity: e.target.value
                })}
                fullWidth
                SelectProps={{ native: true }}
              >
                <option value="common">Common</option>
                <option value="rare">Rare</option>
                <option value="epic">Epic</option>
                <option value="legendary">Legendary</option>
              </TextField>
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  select
                  label="In Stock"
                  value={editingStoreItem.inStock}
                  onChange={(e) => setEditingStoreItem({
                    ...editingStoreItem,
                    inStock: e.target.value === 'true'
                  })}
                  fullWidth
                  SelectProps={{ native: true }}
                >
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </TextField>
                
                <TextField
                  select
                  label="Featured"
                  value={editingStoreItem.featured}
                  onChange={(e) => setEditingStoreItem({
                    ...editingStoreItem,
                    featured: e.target.value === 'true'
                  })}
                  fullWidth
                  SelectProps={{ native: true }}
                >
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </TextField>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditingStoreItem(null);
              setAddingStoreItem(false);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={addingStoreItem ? handleSaveNewStoreItem : handleSaveStoreItem}
            variant="contained"
            color="primary"
          >
            {addingStoreItem ? 'Add Item' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default StoreConfiguration;